/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {
  MouseEvent as ReactMouseEvent,
  MutableRefObject,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import {
  ClockDialView,
  getClockIndexFromPoint,
  getClockMinuteFromPoint,
  isClockOptionEvent
} from './ClockDialUtils';

export interface UseClockDialInteractionsProps {
  onClose: () => void;
  onSelectHour: (hour: number) => void;
  onSelectMinute: (minute: number) => void;
  options: number[];
  view: ClockDialView | null;
}

export interface UseClockDialInteractionsResult {
  faceRef: MutableRefObject<HTMLDivElement | null>;
  onFaceMouseDown: (event: ReactMouseEvent<HTMLDivElement>) => void;
  onOptionClick: (value: number) => (event: ReactMouseEvent<HTMLButtonElement>) => void;
  onOptionMouseDown: (event: ReactMouseEvent<HTMLButtonElement>) => void;
  onRootMouseDown: (event: ReactMouseEvent<HTMLDivElement>) => void;
}

/**
 * Coordinates click, drag, and outside-click interactions for the clock
 * dial.
 *
 * Tracks a single `isDragging` flag, which flips to `true` on a face or
 * drag-handle `mousedown` and back to `false` on document `mouseup`.
 * While dragging, document `mousemove` events resolve the pointer
 * position to a clock value and report it through `onSelectHour` or
 * `onSelectMinute`.
 */
export const useClockDialInteractions = ({
  onClose,
  onSelectHour,
  onSelectMinute,
  options,
  view
}: UseClockDialInteractionsProps): UseClockDialInteractionsResult => {
  const faceRef = useRef<HTMLDivElement | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const selectClockValue = useCallback((value: number) => {
    if (view === ClockDialView.Hours) {
      onSelectHour(value);
    } else {
      onSelectMinute(value);
    }
  }, [
    onSelectHour,
    onSelectMinute,
    view
  ]);

  const selectClockIndex = useCallback((index: number) => {
    const value = options[index];
    if (value !== undefined) {
      selectClockValue(value);
    }
  }, [
    options,
    selectClockValue
  ]);

  const selectClockPoint = useCallback((clientX: number, clientY: number) => {
    if (faceRef.current) {
      if (view === ClockDialView.Minutes) {
        onSelectMinute(getClockMinuteFromPoint(faceRef.current, clientX, clientY));
      } else {
        selectClockIndex(getClockIndexFromPoint(faceRef.current, clientX, clientY));
      }
    }
  }, [
    onSelectMinute,
    selectClockIndex,
    view
  ]);

  const onFaceMouseDown = useCallback((event: ReactMouseEvent<HTMLDivElement>) => {
    if (faceRef.current && !isClockOptionEvent(event.target)) {
      event.preventDefault();
      setIsDragging(true);
      selectClockPoint(event.clientX, event.clientY);
    }
  }, [selectClockPoint]);

  const onRootMouseDown = useCallback((event: ReactMouseEvent<HTMLDivElement>) => {
    if (faceRef.current && !faceRef.current.contains(event.target as Node)) {
      onClose();
    }
  }, [onClose]);

  useEffect(() => {
    if (isDragging) {
      const onDocumentMouseMove = (event: MouseEvent) => {
        selectClockPoint(event.clientX, event.clientY);
      };

      const onDocumentMouseUp = () => {
        setIsDragging(false);
      };

      document.addEventListener('mousemove', onDocumentMouseMove);
      document.addEventListener('mouseup', onDocumentMouseUp);

      return () => {
        document.removeEventListener('mousemove', onDocumentMouseMove);
        document.removeEventListener('mouseup', onDocumentMouseUp);
      };
    }

    return undefined;
  }, [
    isDragging,
    selectClockPoint
  ]);

  const onOptionMouseDown = useCallback((event: ReactMouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
  }, []);

  const onOptionClick = useCallback((value: number) => (event: ReactMouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    selectClockValue(value);
  }, [selectClockValue]);

  return {
    faceRef,
    onFaceMouseDown,
    onOptionClick,
    onOptionMouseDown,
    onRootMouseDown
  };
};

export default useClockDialInteractions;
